<template>
  <b-form enctype="multipart/form-data" class="onboarding-id-form form">
    <div class="page__card page__card--full mt-0">
      <div class="page__card__content pb-0">
        <div class="form__section">
          <b-row class="py-4 px-5">
            <b-col md="6" class="onboarding-id-form__left divider">
              <div class="form__section__header">
                <h4 class="header__title">Your ID</h4>
                <h5 class="header__content">
                  Provide a photo of the front of your drivers license
                </h5>

                <error-message
                  sign=""
                  :error="
                    form.$getError('photo_id_front') || form.$errorMessage
                  "
                ></error-message>
              </div>
              <b-row>
                <b-col md="6" sm="6">
                  <image-cropper
                    ref="photoFrontCropper"
                    v-model="showPhotoFrontUpload"
                    button-label="Choose"
                    :blob-only="false"
                    @change="uploadPhotoFront"
                    @close="showPhotoFrontUpload = false"
                  />
                  <div
                    class="upload-container upload-container--sm mb-4"
                    v-cloak
                    @drop.prevent="onPhotoFrontChange"
                    @dragover.prevent
                  >
                    <b-img
                      v-if="photoFront"
                      :src="photoFront"
                      alt="Avatar"
                      fluid
                      center
                      rounded
                      class="user-id"
                    ></b-img>

                    <b-img
                      v-else
                      :src="require('@/assets/images/id.svg')"
                      alt="Photos"
                      width="180"
                      center
                      class="user-placeholder"
                    ></b-img>
                  </div>
                </b-col>
                <b-col
                  md="6"
                  sm="6"
                  class="upload-button d-flex justify-content-center align-items-center flex-column"
                >
                  <b-button
                    block
                    variant="outline-primary"
                    class="btn--upload upload-container-btn mb-4"
                    :disabled="form.$busy"
                    @click="showPhotoFrontUpload = true"
                    >Upload photo</b-button
                  >

                  <p class="pb-5 upload-container-text">Or drag them in</p>
                </b-col>
              </b-row>
            </b-col>
            <b-col md="6" class="onboarding-id-form__right">
              <div class="form__section__header">
                <h4 class="header__title">Headshot</h4>
                <h5 class="header__content">
                  Add a selfie to verify that your face matches your license
                </h5>

                <error-message
                  sign=""
                  :error="form.$getError('headshot') || form.$errorMessage"
                ></error-message>
              </div>
              <b-row>
                <b-col md="6" sm="6">
                  <image-cropper
                    ref="headshotCropper"
                    v-model="showHeadshotUpload"
                    button-label="Choose"
                    :blob-only="false"
                    @change="uploadHeadshot"
                    @close="showHeadshotUpload = false"
                  />
                  <div
                    class="upload-container upload-container--sm mb-4"
                    v-cloak
                    @drop.prevent="onHeadshotChange"
                    @dragover.prevent
                  >
                    <b-img
                      v-if="headshot"
                      :src="headshot"
                      alt="Avatar"
                      fluid
                      center
                      rounded
                      class="user-id"
                    ></b-img>

                    <b-img
                      v-else
                      :src="require('@/assets/images/headshot.svg')"
                      alt="Photos"
                      width="180"
                      center
                      class="user-placeholder"
                    ></b-img>
                  </div>
                </b-col>
                <b-col
                  md="6"
                  sm="6"
                  class="upload-button d-flex justify-content-center align-items-center flex-column"
                >
                  <b-button
                    block
                    variant="outline-primary"
                    class="btn--upload upload-container-btn mb-4"
                    :disabled="form.$busy"
                    @click="showHeadshotUpload = true"
                    >Upload photo</b-button
                  >
                  <p class="pb-5 upload-container-text">Or drag them in</p>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </div>
      </div>
    </div>
    <div class="page__card page__card--no-bg">
      <div class="page__card__content">
        <div class="form__section">
          <b-button
            type="submit"
            block
            variant="primary"
            :disabled="form.$busy"
            @click="onSubmit"
          >
            <b-spinner small v-if="form.$busy"></b-spinner>
            Continue</b-button
          >
          <b-button block class="btn-no-variant" @click="onSkip">Skip</b-button>
        </div>
      </div>
    </div>
  </b-form>
</template>
<script>
/**
 * ==================================================================================
 * Onboarding ID form
 * ==================================================================================
 **/

import { mapActions, mapGetters } from 'vuex'
import Form from '@/utils/form'
import ImageCropper from '@/components/modals/ImageCropper'
import ErrorMessage from '@/components/inputs/ErrorMessage'

export default {
  components: {
    ImageCropper,
    ErrorMessage
  },
  computed: {
    ...mapGetters({
      userPhotoFront: 'auth/getUserPhotoFront',
      userHeadshot: 'auth/getUserHeadshot'
    }),

    photoFront() {
      if (this.preview.photo_id_front) return this.preview.photo_id_front
      if (this.userPhotoFront) return this.userPhotoFront
      return null
    },

    headshot() {
      if (this.preview.headshot) return this.preview.headshot
      if (this.userHeadshot) return this.userHeadshot
      return null
    }
  },
  data: function() {
    return {
      showPhotoFrontUpload: false,
      showHeadshotUpload: false,
      form: new Form({
        photo_id_front: null,
        headshot: null
      }),
      preview: {
        photo_id_front: null,
        headshot: null
      }
    }
  },
  methods: {
    ...mapActions({
      changePhoto: 'auth/changePhoto'
    }),

    onSkip() {
      this.$router.push({ name: 'home' })
    },

    uploadPhotoFront(data) {
      this.form.photo_id_front = data.blob
      this.preview.photo_id_front = data.image
      this.$root.$emit('uploadComplete')
    },

    onPhotoFrontChange(e) {
      if (this.form.$busy) return
      this.showPhotoFrontUpload = false
      this.$refs.photoFrontCropper.onFileChange(e)
    },

    uploadHeadshot(data) {
      this.form.headshot = data.blob
      this.preview.headshot = data.image
      this.$root.$emit('uploadComplete')
    },

    onHeadshotChange(e) {
      if (this.form.$busy) return
      this.showHeadshotUpload = false
      this.$refs.headshotCropper.onFileChange(e)
    },

    async onSubmit() {
      if (this.form.$busy) return
      this.form.$setLoading(true)

      let post = this.getFormData()
      await this.changePhoto(post)
        .then(() => {
          this.$router.replace({ name: 'home' })
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)
        })
    },

    getFormData() {
      let data = new FormData()

      if (this.form.photo_id_front) {
        data.append('photo_id_front', this.form.photo_id_front)
      }
      if (this.form.headshot) {
        data.append('headshot', this.form.headshot)
      }

      return data
    }
  }
}
</script>
<style scoped lang="scss">
.onboarding-id-form {
  .form__section__header {
    margin: 30px auto;

    .header__title {
      font-weight: 700;
      color: #444a51;
    }
  }

  .form__section {
    .upload-container {
      border: dashed 1px #e3e3e3;

      .user-placeholder {
        width: 100%;
        max-width: 125px;
      }
    }

    .upload-button {
      max-width: 225px;
      margin: 0 auto;
    }
  }

  .divider {
    border-right: 1px solid #e1e6ea;
  }

  &__left {
    padding-right: 3rem;
  }

  &__right {
    padding-left: 3rem;
    padding-bottom: 1.5rem;
  }
}

@media (max-width: 768px) {
  .onboarding-id-form {
    .divider {
      border-right: none;
    }

    .form__section {
      .upload-button {
        max-width: inherit;
      }
    }

    &__left,
    &__right {
      padding-right: 0;
      padding-left: 0;
    }

    &__right {
    }
  }
}

@media (max-width: 576px) {
  .onboarding-id-form {
    .form__section {
      .upload-button {
        max-width: inherit;
      }
    }
  }
}
</style>
